import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { useSelectedTextFrom911ConversationID } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import {
  selectConversationState,
  selectConversationsForCallerId,
  selectTranslationLanguages,
} from './chatsSlice';
import { ChatType, ConversationState, TranslationLanguage } from './chatsTypes';

export const useChatTypeConversations = (): Record<
  ChatType,
  ConversationState | undefined
> => {
  const callerId = useSelectedCallerID();
  const conversations = useSelector(selectConversationsForCallerId(callerId));

  return {
    agencyShare: conversations.find(c => c.conversationId.startsWith('acs')),
    textFrom911: conversations.find(c => c.conversationId.startsWith('tf911')),
    rcs: conversations.find(c => c.conversationId.startsWith('rcs')),
    conversations: conversations.find(
      c =>
        c.conversationId.startsWith('tcapi') ||
        c.conversationId.startsWith('admin_sms'),
    ),
    collaborator: conversations.find(c =>
      c.conversationId.startsWith('collaborator'),
    ),
  };
};

// A conversation that has ended keeps its language in the chats slice, and the
// caller-to-conversation mapping it was found through is persisted, so both
// outlive the conversation itself. Its language is history: the chat panel
// renders no translation menu for an ended conversation either.
const LIVE_STATUSES: ConversationState['status'][] = ['active', 'pending'];

/**
 * The citizen language saved on a caller's *current, live* Text-From-911
 * conversation, resolved against the supported-language list so it can be
 * handed to the language pickers.
 *
 * Resolves the conversation the same way ChatContext does — caller id to
 * conversation id through the textFrom911 slice — so the language here and the
 * one in the chat panel's translation menu cannot disagree. Reading it off the
 * chats slice by caller id instead would match any conversation with that
 * caller, and an earlier ended one would outvote the live conversation.
 *
 * Null until the language list has loaded, so a caller of this hook that
 * mirrors it into local state will pick the language up when the list arrives.
 */
export const useTF911CallerTranslationLanguage = (
  callerId: string | null | undefined,
): TranslationLanguage | null => {
  const conversationId = useSelectedTextFrom911ConversationID(callerId ?? '');
  const { overrideCallerLanguage, status } = useSelector(
    selectConversationState(conversationId),
  );
  const languages = useSelector(selectTranslationLanguages);

  const languageCode = LIVE_STATUSES.includes(status)
    ? overrideCallerLanguage
    : undefined;

  return useMemo(
    () => languages.find(l => l.languageCode === languageCode) ?? null,
    [languages, languageCode],
  );
};

export const useOpenChatTypes = (): Record<ChatType, boolean> => {
  const conversations = useChatTypeConversations();

  return {
    agencyShare: !conversations.agencyShare?.isCollapsed,
    textFrom911: !conversations.textFrom911?.isCollapsed,
    rcs: !conversations.rcs?.isCollapsed,
    conversations: !conversations.conversations?.isCollapsed,
    collaborator: !conversations.collaborator?.isCollapsed,
  };
};
